import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Box, Button, Card, Chip, Grid, InputLabel, MenuItem, FormControl, Select, Typography } from '@mui/material';
import { DataGrid, getGridStringOperators } from '@mui/x-data-grid';
import { Phone, PhoneForwarded, PhoneMissed, Group, Person } from '@mui/icons-material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { BookingService } from '../Booking/Service';
import { formatDateTime } from '../../utils/dateUtils';

const statCards = [
    { key: 'total', label: 'Total Calls', color: '#1565c0', icon: <Phone /> },
    { key: 'connected', label: 'Connected', color: '#2e7d32', icon: <PhoneForwarded /> },
    { key: 'missed', label: 'Missed', color: '#d32f2f', icon: <PhoneMissed /> },
    { key: 'customer', label: 'To Customer', color: '#c62828', icon: <Person /> },
    { key: 'provider', label: 'To Expert', color: '#1565c0', icon: <Group /> },
];

const statusColor = {
    initiated: '#fb8c00',
    dialing: '#1565c0',
    answered: '#2e7d32',
    ended: '#6a1b9a',
    failed: '#d32f2f',
    completed: '#2e7d32',
};

const MyOperatorDashboard = () => {
    const [stats, setStats] = useState(null);
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedAdmin, setSelectedAdmin] = useState('all');
    const [selectedDate, setSelectedDate] = useState(dayjs());

    const fetchData = useCallback(async () => {
        setLoading(true);
        const params = {
            startDate: selectedDate.startOf('day').format('YYYY-MM-DD'),
            endDate: selectedDate.endOf('day').format('YYYY-MM-DD'),
        };
        const [statsRes, adminsRes] = await Promise.all([
            BookingService.getMyoperatorStats(params),
            BookingService.getMyoperatorAdmins(),
        ]);
        if (statsRes?.success === 1) {
            setStats(statsRes.data);
        }
        if (adminsRes?.success === 1) {
            setAdmins(adminsRes.data);
        }
        setLoading(false);
    }, [selectedDate]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const configuredAdmins = useMemo(() => {
        return admins.filter(a => a.configured);
    }, [admins]);

    const adminList = useMemo(() => {
        return configuredAdmins.map(a => a.name).filter(Boolean).sort();
    }, [configuredAdmins]);

    const adminCounts = useMemo(() => {
        if (!stats?.calls) return {};
        const counts = {};
        stats.calls.forEach(c => {
            const name = c.initiatedByName || 'Unknown';
            counts[name] = (counts[name] || 0) + 1;
        });
        return counts;
    }, [stats]);

    const filteredCalls = useMemo(() => {
        if (!stats?.calls) return [];
        if (selectedAdmin === 'all') return stats.calls;
        return stats.calls.filter(c => c.initiatedByName === selectedAdmin);
    }, [stats, selectedAdmin]);

    const containsOnlyOperator = getGridStringOperators()
        .filter(operator => operator.value === 'contains')
        .map(operator => ({
            ...operator, requiresOperator: false,
            InputComponent: operator.InputComponent,
            HeaderComponent: null, headerLabel: null, showOperatorSelector: false
        }));

    const columns = [
        {
            field: 'createdAt', headerName: 'Time', width: 110,
            renderCell: (params) => (
                <Typography sx={{ fontSize: '.65rem' }}>{formatDateTime(params.value)}</Typography>
            ),
        },
        {
            field: 'target', headerName: 'Type', width: 100,
            renderCell: (params) => (
                <Chip label={params.value === 'provider' ? 'EXPERT' : 'CUSTOMER'} size="small"
                    sx={{ fontSize: '0.6rem', fontWeight: 600, height: 20,
                        bgcolor: params.value === 'provider' ? '#e3f2fd' : '#fce4ec',
                        color: params.value === 'provider' ? '#1565c0' : '#c62828' }}
                />
            ),
        },
        {
            field: 'name', headerName: 'Name', flex: 1, minWidth: 120,
            renderCell: (params) => (
                <Typography sx={{ fontSize: '.65rem' }}>{params.value}</Typography>
            ),
            valueGetter: (params) => params.row.customerName || params.row.providerName || params.row.customerPhone,
            filterOperators: containsOnlyOperator,
        },
        {
            field: 'customerPhone', headerName: 'Phone', width: 120,
            renderCell: (params) => (
                <Typography sx={{ fontSize: '.65rem' }}>{params.value}</Typography>
            ),
        },
        {
            field: 'bookingNumber', headerName: 'Booking', width: 130,
            renderCell: (params) => params.value ? (
                <Typography variant="body2" color="primary"
                    sx={{ fontSize: '.65rem', cursor: 'pointer' }}
                    onClick={() => window.open(`/admin/booking-details/${params.row.bookingId}`, '_blank')}
                >
                    {params.value}
                </Typography>
            ) : '-',
            filterOperators: containsOnlyOperator,
        },
        {
            field: 'duration', headerName: 'Duration', width: 90,
            renderCell: (params) => (
                <Typography sx={{ fontSize: '.65rem' }}>
                    {params.value > 0 ? `${Math.floor(params.value / 60)}m ${params.value % 60}s` : '-'}
                </Typography>
            ),
        },
        {
            field: 'status', headerName: 'Status', width: 100,
            renderCell: (params) => (
                <Chip label={params.value} size="small"
                    sx={{ fontSize: '0.6rem', fontWeight: 600, height: 20,
                        bgcolor: `${statusColor[params.value]}18`,
                        color: statusColor[params.value] }}
                />
            ),
        },
        {
            field: 'initiatedByName', headerName: 'Initiated By', width: 120,
            renderCell: (params) => (
                <Typography sx={{ fontSize: '.65rem' }}>{params.value || '-'}</Typography>
            ),
            filterOperators: containsOnlyOperator,
        },
        {
            field: 'error', headerName: 'Error', flex: 1, minWidth: 150,
            renderCell: (params) => params.row.status === 'failed' && params.row.errorDetails?.response?.details ? (
                <Typography sx={{ fontSize: '.65rem', color: '#d32f2f' }}>{params.row.errorDetails.response.details}</Typography>
            ) : '-',
        },
    ];

    return (
        <Box sx={{ p: 2 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '1.1rem', mb: 2 }}>MyOperator Dashboard</Typography>

            {loading ? (
                <Typography sx={{ color: '#9e9e9e' }}>Loading...</Typography>
            ) : stats ? (
                <>
                    <Grid container spacing={1.5} sx={{ mb: 2 }}>
                        {statCards.map(({ key, label, color }) => (
                            <Grid item xs={6} md={2.4} key={key}>
                                <Card sx={{ p: 1.5, textAlign: 'center', borderLeft: `3px solid ${color}` }}>
                                    <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color }}>{stats[key] ?? 0}</Typography>
                                    <Typography sx={{ fontSize: '0.7rem', color: '#757575' }}>{label}</Typography>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    <Card sx={{ p: 2, mb: 2 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 1.5 }}>Configured Admins ({configuredAdmins.length})</Typography>
                        {configuredAdmins.length === 0 ? (
                            <Typography sx={{ fontSize: '0.75rem', color: '#9e9e9e' }}>No admins configured</Typography>
                        ) : (
                            <Grid container spacing={1}>
                                {configuredAdmins.map(admin => (
                                    <Grid item xs={6} sm={4} md={2.4} key={admin.id}>
                                        <Card sx={{ p: 1, textAlign: 'center', bgcolor: '#f5f5f5' }}>
                                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 500 }}>{admin.name}</Typography>
                                            <Typography sx={{ fontSize: '0.6rem', color: '#757575' }}>{admin.email}</Typography>
                                            <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                                                <Chip label={`ID: ${admin.myOperatorUserId}`} size="small"
                                                    sx={{ fontSize: '0.5rem', fontWeight: 600, height: 16,
                                                        bgcolor: '#e3f2fd', color: '#1565c0' }}
                                                />
                                                <Chip label={`${adminCounts[admin.name] || 0} calls`} size="small"
                                                    sx={{ fontSize: '0.5rem', fontWeight: 600, height: 16,
                                                        bgcolor: (adminCounts[admin.name] || 0) > 0 ? '#e8f5e9' : '#f5f5f5',
                                                        color: (adminCounts[admin.name] || 0) > 0 ? '#2e7d32' : '#9e9e9e' }}
                                                />
                                            </Box>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Card>

                    <Card sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>Call List</Typography>
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                                <InputLabel sx={{ fontSize: '0.75rem' }}>Filter by Admin</InputLabel>
                                <Select
                                    value={selectedAdmin}
                                    label="Filter by Admin"
                                    onChange={(e) => setSelectedAdmin(e.target.value)}
                                    sx={{ fontSize: '0.75rem' }}
                                >
                                    <MenuItem value="all" sx={{ fontSize: '0.75rem' }}>All Admins ({stats.calls?.length || 0})</MenuItem>
                                    {adminList.map(name => (
                                        <MenuItem key={name} value={name} sx={{ fontSize: '0.75rem' }}>
                                            {name} ({adminCounts[name] || 0})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Box sx={{ flexGrow: 1 }} />
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DesktopDatePicker
                                    label="Select Date"
                                    value={selectedDate}
                                    onChange={(newValue) => newValue && setSelectedDate(newValue)}
                                    format="DD/MM/YYYY"
                                    slotProps={{ textField: { size: 'small', sx: { '& .MuiInputBase-input': { fontSize: '0.75rem', py: 1 } } } }}
                                />
                            </LocalizationProvider>
                            <Button size="small" variant="outlined" sx={{ fontSize: '0.7rem', textTransform: 'none' }} onClick={() => setSelectedDate(dayjs())}>Today</Button>
                        </Box>
                        <DataGrid
                            rows={filteredCalls}
                            columns={columns}
                            getRowId={(row) => row._id || row.id}
                            pageSizeOptions={[10, 25, 50]}
                            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                            disableRowSelectionOnClick
                            sx={{
                                minWidth: '100%',
                                '& .MuiDataGrid-root': { overflowX: 'auto' },
                                '& .MuiDataGrid-footerContainer': { display: 'block' },
                                '& .MuiDataGrid-row': { '& .MuiDataGrid-cell': { py: 1 } },
                                '& .MuiDataGrid-columnHeaders': {
                                    borderBottom: '2px solid rgba(224, 224, 224, 1)',
                                    borderTop: '2px solid rgba(224, 224, 224, 1)',
                                    fontWeight: 600, fontSize: '0.75rem', p: 0,
                                    minHeight: '0 !important', height: '2rem'
                                },
                                '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600, color: '#3e3e3e' },
                            }}
                        />
                    </Card>
                </>
            ) : (
                <Typography sx={{ color: '#d32f2f' }}>Failed to load stats</Typography>
            )}
        </Box>
    );
};

export default MyOperatorDashboard;
